import { RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { DataTableDensity } from '~components/DataTable/dataTableColumn';
import {
  columnFloorVariableName,
  columnWidthVariableName,
} from '~components/DataTable/dataTableColumnVariables';
import { CELL_HORIZONTAL_PADDING_PX } from '~components/DataTable/getColumnSlots';

const CONTENT_PADDING_PX = CELL_HORIZONTAL_PADDING_PX * 2;
const WIDTH_VARIABLE_PREFIX = columnWidthVariableName('');

// The widest each column's content is on the page now showing, cell padding included
// and capped like the cell itself. Each measured element is an inline span (or an
// inline-flex one for custom cells), so its width is the content's own width, not the
// column's, and leftover width the column was given never becomes part of its floor.
function measureContentWidths(table: HTMLElement, caps: Record<string, number>) {
  const widths = new Map<string, number>();

  table.querySelectorAll<HTMLElement>('[data-floor-field]').forEach((content) => {
    const field = content.dataset.floorField;
    const isInNestedTable = content.closest('table') !== table;

    if (!field || caps[field] === undefined || isInNestedTable) {
      return;
    }

    const contentWidth = Math.ceil(content.getBoundingClientRect().width);
    const width = Math.min(contentWidth + CONTENT_PADDING_PX, caps[field]);
    widths.set(field, Math.max(widths.get(field) ?? 0, width));
  });

  return widths;
}

// Holds every column at the widest its content has been on any page shown so far, so
// paging to shorter values doesn't move the column boundaries. Floors are CSS variables
// written straight onto the table, so growing one re-renders nothing.
export default function useColumnFloors(
  tableRef: RefObject<HTMLTableElement | null>,
  rows: unknown,
  visibleFieldsKey: string,
  caps: Record<string, number>,
  density: DataTableDensity,
) {
  const floorsRef = useRef(new Map<string, number>());
  const measuredDensityRef = useRef(density);

  const growFloors = useCallback(() => {
    const table = tableRef.current;

    if (!table) {
      return;
    }

    measureContentWidths(table, caps).forEach((width, field) => {
      if (width > (floorsRef.current.get(field) ?? 0)) {
        floorsRef.current.set(field, width);
        table.style.setProperty(columnFloorVariableName(field), `${width}px`);
      }
    });
  }, [caps, tableRef]);

  const clearFloors = useCallback(() => {
    const table = tableRef.current;

    floorsRef.current.forEach((_width, field) =>
      table?.style.removeProperty(columnFloorVariableName(field)),
    );
    floorsRef.current.clear();
  }, [tableRef]);

  useLayoutEffect(() => {
    const didDensityChange = measuredDensityRef.current !== density;

    if (didDensityChange) {
      measuredDensityRef.current = density;
      clearFloors();
    }

    growFloors();
  }, [clearFloors, density, growFloors, rows, visibleFieldsKey]);

  // Text measured before the web font loads is measured in the fallback font.
  useEffect(() => {
    let isMounted = true;

    if ('fonts' in document) {
      void document.fonts.ready.then(() => {
        if (isMounted) {
          growFloors();
        }
      });
    }

    return () => {
      isMounted = false;
    };
  }, [growFloors]);

  // A dragged width caps custom content at the dragged size, so it is cleared before
  // measuring. React drops it from the table's style on the next render anyway.
  const refitColumn = useCallback(
    (field: string) => {
      const table = tableRef.current;

      if (!table) {
        return;
      }

      table.style.removeProperty(columnWidthVariableName(field));
      table.style.removeProperty(columnFloorVariableName(field));
      floorsRef.current.delete(field);
      growFloors();
    },
    [growFloors, tableRef],
  );

  const resetFloors = useCallback(() => {
    const table = tableRef.current;

    if (!table) {
      return;
    }

    const draggedWidthVariables = Array.from(table.style).filter((property) =>
      property.startsWith(WIDTH_VARIABLE_PREFIX),
    );
    draggedWidthVariables.forEach((property) => table.style.removeProperty(property));
    clearFloors();
    growFloors();
  }, [clearFloors, growFloors, tableRef]);

  return { growFloors, refitColumn, resetFloors };
}
